/**
 * Lista e punëtorëve në Excel.
 *
 * Ishte CSV: një varg me presje, pa kokë kompanie, pa datë, pa gjerësi kolonash.
 * Hapej te Excel-i me diakritikat e prishura ose me tërë rreshtin në një qelizë,
 * varësisht si e merrte makina lokalja — dhe numri personal, që nis me zero,
 * kthehej në numër dhe e humbte atë zero. Skedari nuk ishte dokument; ishte një
 * dump që dikush duhej ta rregullonte para se ta dërgonte.
 *
 * Faqosja vjen nga `reportSheetExport`, e njëjta që përdorin Lista e Pagave dhe
 * raportet — pra kjo listë del me të njëjtën gjuhë vizuale si çdo dokument
 * tjetër i sistemit, jo si përjashtim.
 *
 * Ndërtimi rri i ndarë nga shkarkimi (`buildEmployeesSheet` kundrejt
 * `exportEmployeesExcel`) që kolonat të provohen pa hapur asnjë skedar.
 */
import { exportSheetExcel } from './reportSheetExport';

/** Emri i llojit të punësimit nga id-ja; teksti i lirë mbetet ashtu siç është. */
export function workTypeName(val, workTypes = []) {
  if (val === null || val === undefined || val === '') return '';
  const n = Number(val);
  if (!Number.isNaN(n)) {
    const wt = (workTypes || []).find((x) => String(x.id ?? x.Id) === String(n));
    return wt?.name || wt?.Name || String(val);
  }
  return String(val);
}

/**
 * Profesioni kur fusha është bosh — nxjerrë nga kualifikimi.
 * Ishte brenda ndërtuesit të CSV-së; njësoj si atje, vetëm katër rastet e njohura.
 */
export function professionOf(row = {}) {
  if (row.Profession) return String(row.Profession);
  const q = String(row.Qualification || '').toLowerCase();
  if (q.includes('matemat'))    return 'Matematikan';
  if (q.includes('informatik')) return 'Programer / Inxhinier Softueri';
  if (q.includes('ekonom'))     return 'Ekonomist';
  if (q.includes('jurid'))      return 'Jurist';
  return row.WorkType ? String(row.WorkType) : '';
}

/**
 * Përshkrimi i fletës — kolonat, rreshtat dhe koka.
 *
 * @param {Array}  rows              punëtorët e filtruar, ashtu si duken në ekran
 * @param {object} opts
 *   - workTypes      lista e llojeve të punësimit (id → emër)
 *   - exportFields   fushat e personalizuara që dalin te eksporti
 *   - cfValues       { workerId: { fieldKey: value } }
 *   - formatCustom   (field, value) → tekst
 *   - formatDate     (value) → tekst
 *   - company        { CompanyName, Logo }
 *   - labels         etiketat e përkthyera; parazgjedhja shqip
 */
export function buildEmployeesSheet(rows = [], opts = {}) {
  const {
    workTypes = [], exportFields = [], cfValues = {},
    formatCustom = (_f, v) => (v == null ? '' : String(v)),
    formatDate = (v) => (v ? String(v) : ''),
    company = {}, labels = {}, today = '',
  } = opts;

  const L = {
    title: 'LISTA E PUNËTORËVE',
    name: 'Emri dhe mbiemri', personalId: 'Numri personal',
    start: 'Data e fillimit', end: 'Data e përfundimit',
    email: 'Email', place: 'Vendbanimi', phone: 'Telefoni',
    workType: 'Lloji i punësimit', qualification: 'Niveli i shkollimit',
    profession: 'Profesioni', position: 'Pozita',
    total: 'Gjithsej punëtorë', generated: 'Lëshuar më',
    ...labels,
  };

  const core = [
    { key: 'name',          label: L.name,          w: 24 },
    // Numri personal si TEKST: nis me zero te shumica e rasteve, dhe si numër
    // Excel-i ia heq atë zero pa pyetur.
    { key: 'personalId',    label: L.personalId,    w: 15 },
    { key: 'start',         label: L.start,         w: 13 },
    { key: 'end',           label: L.end,           w: 13 },
    { key: 'email',         label: L.email,         w: 24 },
    { key: 'place',         label: L.place,         w: 18, muted: true },
    { key: 'phone',         label: L.phone,         w: 14 },
    { key: 'workType',      label: L.workType,      w: 16, muted: true },
    { key: 'qualification', label: L.qualification, w: 18, muted: true },
    { key: 'profession',    label: L.profession,    w: 20, muted: true },
    { key: 'position',      label: L.position,      w: 18 },
  ];
  const custom = exportFields.map((f) => ({ key: `cf_${f.key}`, label: f.label, w: 16, muted: true }));

  const body = (rows || []).map((r) => {
    const id = String(r?.Id ?? r?.id ?? '');
    const vals = cfValues[id] || {};
    const row = {
      name: `${r?.FirstName || ''} ${r?.LastName || ''}`.trim(),
      personalId: r?.PersonalId || '',
      start: formatDate(r?.StartDate) || '',
      end: formatDate(r?.EndDate) || '',
      email: r?.Email || '',
      place: r?.Place || r?.Address || '',
      phone: r?.Phone || '',
      workType: workTypeName(r?.WorkType ?? r?.EmploymentType, workTypes),
      qualification: r?.Qualification || '',
      profession: professionOf(r),
      position: r?.Position || '',
    };
    exportFields.forEach((f) => { row[`cf_${f.key}`] = formatCustom(f, vals[f.key]); });
    return row;
  });

  return {
    title: L.title,
    logo: company.Logo || null,
    left: [company.CompanyName || company.name || ''].filter(Boolean),
    right: [`${L.total}: ${body.length}`, today ? `${L.generated}: ${today}` : ''].filter(Boolean),
    columns: [...core, ...custom],
    body,
    sheetName: 'Punëtorët',
    fileBase: today ? `lista_e_punetoreve_${today}` : 'lista_e_punetoreve',
    orientation: 'landscape',
  };
}

/** Ndërto dhe shkarko. */
export function exportEmployeesExcel(rows, opts) {
  exportSheetExcel(buildEmployeesSheet(rows, opts));
}
